import React, { forwardRef } from 'react';
import { View } from 'react-native';
import { Pressable } from '@/components/gluestack/pressable';
import { HStack } from '@/components/gluestack/hstack';
import { VStack } from '@/components/gluestack/vstack';
import { Text } from '@/components/gluestack/text';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { Badge, BadgeText } from '@/components/gluestack/badge';
import type { HugeiconsProps } from '@hugeicons/react-native';
import { ArrowRight01Icon } from '@hugeicons-pro/core-stroke-rounded';

export interface ISettingsItemProps {
  label: string | React.ReactNode;
  onPress?: () => void;
  className?: string;
  icon?: HugeiconsProps['icon'];
  iconClassName?: string;
  labelClassName?: string;
  rightIcon?: HugeiconsProps['icon'] | null;
  disabled?: boolean;
  badge?: string | React.ReactNode;
  badgeClassName?: string;
  hint?: string;
}

export const SettingsItem = forwardRef<View, ISettingsItemProps>(
  (
    {
      label,
      onPress,
      className,
      icon: LeftIcon,
      iconClassName,
      labelClassName,
      rightIcon: RightIcon = ArrowRight01Icon,
      disabled,
      badge,
      badgeClassName,
      hint,
    },
    ref
  ) => {
    const Container: any = onPress ? Pressable : View;

    return (
      <Container
        ref={ref}
        onPress={onPress}
        accessibilityRole={onPress ? 'button' : undefined}
        hitSlop={onPress ? 8 : undefined}
        disabled={disabled}
        className={`px-2 ${onPress && RightIcon ? 'pr-0' : ''} py-2 -mx-2 ${
          onPress ? 'web:cursor-pointer active:opacity-70 active:bg-background-50 active:dark:bg-background-0 web:hover:bg-background-50 web:hover:dark:bg-background-0' : ''
        } rounded-xl ${className ?? ''}`}
      >
        <VStack className="gap-1">
          <HStack className="items-center justify-between gap-2 mr-2">
            <HStack className="flex-1 items-start gap-2">
              {LeftIcon ? (
                <HugeIconsWrapper icon={LeftIcon} size={22} className={iconClassName ?? 'color-typography-500'} />
              ) : null}
              <HStack className="flex-1 flex-row flex-wrap items-center gap-1">
                {typeof label === 'string' ? (
                  <Text isTruncated className={`min-w-0 ${onPress ? 'font-semibold' : ''} ${labelClassName ?? ''}`}>{label}</Text>
                ) : (
                  label
                )}
                <View className="flex-1 web:hidden" />
                {badge ? (
                  typeof badge === 'string' ? (
                    <Badge variant="solid" size="lg" action="muted" className={`ml-auto rounded-md flex-shrink bg-background-100 ${badgeClassName ?? ''}`}>
                      <BadgeText className="normal-case" numberOfLines={1}>{badge.replace(/[\r\n]+/g, ' ').trim()}</BadgeText>
                    </Badge>
                  ) : (
                    <View className={`ml-auto flex-shrink-0 ${badgeClassName ?? ''}`}>{badge}</View>
                  )
                ) : null}
              </HStack>
            </HStack>
            {onPress && RightIcon ? (
              <HugeIconsWrapper icon={RightIcon} size={20} className="color-background-300" />
            ) : null}
          </HStack>
          {hint ? (
            <Text size="sm" className="text-typography-400 ml-[30px] mt-1">{hint}</Text>
          ) : null}
        </VStack>
      </Container>
    );
  }
);

SettingsItem.displayName = 'SettingsItem';


